/**
 * User Preferences Types
 *
 * Type definitions for user preferences matching the API response structure.
 * These types are used by UserPreferencesService and related stores.
 */

import type { WorkoutDay, MeasurementSystem, Language } from './auth';
import type { DashboardProfile, Theme } from './index';

// =============================================================================
// CURRENCY
// =============================================================================

/** A currency the account and its contracts can be priced in — mirrors `App\Enums\Currency`. */
export type Currency = 'CAD' | 'USD' | 'EUR' | 'GBP' | 'JPY';

export const CURRENCIES: readonly Currency[] = [
  'CAD',
  'USD',
  'EUR',
  'GBP',
  'JPY',
] as const;

/**
 * Digits after the decimal point in each currency's smallest unit (ISO 4217):
 * two for the dollars, euro and pound, none for the yen. The API stores every
 * fee as `fee × 100` and shows it back in the currency, so a fraction of a yen
 * would be kept but never shown. Keyed by the union, so a currency the API
 * adds fails type-check here.
 */
export const CURRENCY_MINOR_DIGITS: Record<Currency, 0 | 2> = {
  CAD: 2,
  USD: 2,
  EUR: 2,
  GBP: 2,
  JPY: 0,
};

/**
 * The symbol printed before an amount. Fixed rather than locale-chosen, so the
 * same figure reads the same everywhere; the two dollars share `$`, which is
 * why a figure that sits beside another currency also carries its code.
 */
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  CAD: '$',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
};

/** The server's fallback when an account has no currency preference. */
export const DEFAULT_CURRENCY: Currency = 'CAD';

// =============================================================================
// USER PREFERENCES INTERFACE
// =============================================================================

/**
 * User preferences interface matching API response structure
 * This represents the complete preferences object returned from the API
 * and stored on the User object.
 */
export interface UserPreferences {
  /** Theme selection: 'light', 'dark', or 'auto' */
  theme_selection: Theme;

  /** Measurement system: 'metric' or 'imperial' */
  measurement_system: MeasurementSystem;

  /** Preferred language code (e.g., 'en', 'es', 'fr') */
  language: Language;

  /** Dashboard profile layout: 'default', 'social', 'analytics', 'compact', or 'trainer' */
  dashboard_profile: DashboardProfile;

  /** Number of workouts per week (1-7), nullable if not set */
  workouts_per_week: number | null;

  /** Preferred workout days array */
  preferred_workout_days: WorkoutDay[];

  /** Notification preferences - key-value pairs for notification settings */
  notification_preferences: Record<string, boolean>;

  /** Privacy settings - key-value pairs for privacy options */
  privacy_settings: Record<string, string | boolean>;

  /**
   * The account's currency. The session payload always carries it (CAD when
   * unset); optional here only because no screen had read it before.
   */
  currency?: Currency;

  /**
   * The IANA timezone a device last reported for the account, e.g.
   * `America/Vancouver`; null until one has. Optional because fixtures and
   * payloads from before it was added do not carry it.
   */
  detected_timezone?: string | null;
}

// =============================================================================
// UPDATE REQUEST INTERFACE
// =============================================================================

/**
 * Update user preferences request interface for PATCH/PUT requests
 * All fields are optional since users can update individual preferences
 */
export interface UpdateUserPreferencesRequest {
  /** Theme selection: 'light', 'dark', or 'auto' */
  theme_selection?: Theme;

  /** Measurement system: 'metric' or 'imperial' */
  measurement_system?: MeasurementSystem;

  /** Preferred language code (e.g., 'en', 'es', 'fr') */
  language?: Language;

  /** Dashboard profile layout: 'default', 'social', 'analytics', 'compact', or 'trainer' */
  dashboard_profile?: DashboardProfile;

  /** Number of workouts per week (1-7), nullable if not set */
  workouts_per_week?: number | null;

  /** Preferred workout days array */
  preferred_workout_days?: WorkoutDay[];

  /** Notification preferences - key-value pairs for notification settings */
  notification_preferences?: Record<string, boolean>;

  /** Privacy settings - key-value pairs for privacy options */
  privacy_settings?: Record<string, string | boolean>;

  /**
   * The device's IANA timezone (`getDeviceTimeZone()`), never an offset. The
   * API checks it against PHP's timezone identifiers.
   */
  detected_timezone?: string | null;
}

// =============================================================================
// RESPONSE INTERFACES
// =============================================================================

/**
 * Response from GET /api/user/preferences endpoint
 */
export interface GetUserPreferencesResponse {
  data: UserPreferences;
}

/**
 * Response from PATCH /api/user/preferences endpoint
 * Returns 204 No Content on success, so this is typically void
 */
export type UpdateUserPreferencesResponse = void;

// =============================================================================
// NOTIFICATION PREFERENCES
// =============================================================================

/**
 * Standard notification preference keys used in the application
 * These are the most common notification settings
 */
export interface StandardNotificationPreferences {
  /**
   * Enable/disable the scheduled report emails (quarterly progress, monthly
   * goal, quarterly program efficacy). Transactional email is not governed.
   */
  email_reports?: boolean;

  /** Enable/disable push notifications */
  push_notifications?: boolean;

  /** Enable/disable workout reminders */
  workout_reminders?: boolean;

  /** Enable/disable progress updates */
  progress_updates?: boolean;

  /** Enable/disable achievement notifications */
  achievement_notifications?: boolean;

  /** Enable/disable social notifications (friend activity, etc.) */
  social_notifications?: boolean;

  /** Enable/disable challenge notifications */
  challenge_notifications?: boolean;

  /** Enable/disable goal milestone notifications */
  goal_notifications?: boolean;
}

// =============================================================================
// PRIVACY SETTINGS
// =============================================================================

/**
 * Standard privacy setting keys used in the application
 */
export interface StandardPrivacySettings {
  /** Profile visibility: 'public', 'friends', or 'private' */
  profile_visibility?: 'public' | 'friends' | 'private';

  /** Whether to show workout activity in social feeds */
  show_workout_activity?: boolean;

  /** Whether to show achievements publicly */
  show_achievements?: boolean;

  /** Whether to show on leaderboards */
  show_on_leaderboards?: boolean;

  /** Whether to allow friend requests */
  allow_friend_requests?: boolean;

  /** Whether to show progress stats */
  show_progress_stats?: boolean;
}

/**
 * One thing a grant holder — a friend, or a trainer — can or cannot see, read
 * off the user's privacy settings. What the grant band on the Friends and
 * Trainers screens draws as a badge, on web and mobile alike.
 */
export interface GrantItem {
  /** The `privacy_settings` key this reads. */
  key: string;
  /** Short label for the badge. */
  label: string;
  /** Phrase for use inside a prose sentence, lower case. */
  phrase: string;
  /** Whether the grant holders can currently see it. */
  visible: boolean;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a value is a valid UserPreferences object
 */
export function isUserPreferences(value: unknown): value is UserPreferences {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const obj = value as Record<string, unknown>;

  return (
    'theme_selection' in obj &&
    'measurement_system' in obj &&
    'language' in obj &&
    'dashboard_profile' in obj &&
    'workouts_per_week' in obj &&
    'preferred_workout_days' in obj &&
    'notification_preferences' in obj &&
    'privacy_settings' in obj
  );
}

/**
 * Type guard to check if a value is a valid partial preferences update
 */
export function isUpdateUserPreferencesRequest(
  value: unknown
): value is UpdateUserPreferencesRequest {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  // For update requests, any subset of fields is valid
  const validKeys = new Set([
    'theme_selection',
    'measurement_system',
    'language',
    'dashboard_profile',
    'workouts_per_week',
    'preferred_workout_days',
    'notification_preferences',
    'privacy_settings',
    'detected_timezone',
  ]);

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);

  // At least one valid key must be present
  if (keys.length === 0) {
    return false;
  }

  // All keys must be valid preference keys
  return keys.every((key) => validKeys.has(key));
}

// =============================================================================
// DEFAULT VALUES
// =============================================================================

/**
 * Default user preferences for new users
 */
export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  theme_selection: 'auto',
  measurement_system: 'metric',
  language: 'en',
  dashboard_profile: 'default',
  workouts_per_week: null,
  preferred_workout_days: [],
  notification_preferences: {},
  privacy_settings: {},
};
